import { useNavigate } from 'react-router-dom';
import { Tag, DollarSign, CalendarDays, Package, ShieldCheck } from 'lucide-react';
import {
  Product,
  ProductConnection,
  ProductReviewStatus,
  ProductStatus,
  ProductType,
} from '@shopify-multiseller/graphql/partner/types';
import { PRODUCTS_QUERY } from './queries';
import { AdminUI } from '../../ui/AdminUI';
import { Button } from '../../components/primitives';
import { DataList, type DataListColumn } from '../../components/DataList';
import { CommandHeader } from '../../components/CommandHeader';
import { FilterBuilder, type AppliedFilters, type FilterField } from '../../components/FilterBuilder';
import { presetDateIso } from '../../components/FilterBuilder/presetDate';
import { ProductStateBadge } from '../../components/StatusBadge/ProductStateBadge';
import { Thumb } from '../../components/Thumb';
import { useListView } from '../../hooks/useListView';
import { useShopCurrency } from '../../hooks';
import { formatCents, formatRelativeTime } from '../../helpers/formatters';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';

type ProductsData = { productConnection: ProductConnection };

const selectProductConnection = (data?: ProductsData) => data?.productConnection;

const FILTER_FIELDS: FilterField[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'enum',
    multiple: true,
    icon: Tag,
    options: [
      { value: ProductStatus.Active, label: 'Active' },
      { value: ProductStatus.New, label: 'New' },
      { value: ProductStatus.Draft, label: 'Offline' },
      { value: ProductStatus.Archived, label: 'Archived' },
    ],
  },
  {
    // Where the product stands with the shop, which is not the same axis as
    // whether it is live: an active product can have edits waiting. This is
    // now the only way to reach those states — the heading used to count them
    // and review asked for that row to go.
    key: 'review',
    label: 'Review',
    type: 'enum',
    multiple: true,
    icon: ShieldCheck,
    options: [
      { value: ProductReviewStatus.Pending, label: 'Awaiting approval' },
      { value: ProductReviewStatus.ChangesPending, label: 'Changes pending review' },
      { value: ProductReviewStatus.ChangesRejected, label: 'Changes requested' },
      { value: ProductReviewStatus.Rejected, label: 'Rejected' },
      { value: ProductReviewStatus.Approved, label: 'Approved' },
    ],
  },
  {
    key: 'productType',
    label: 'Type',
    type: 'enum',
    icon: Package,
    options: [
      { value: ProductType.Physical, label: 'Physical' },
      { value: ProductType.Digital, label: 'Digital' },
    ],
  },
  { key: 'price', label: 'Price', type: 'number', prefix: '$', icon: DollarSign },
  {
    key: 'created',
    label: 'Created',
    type: 'date',
    icon: CalendarDays,
    presets: [
      { label: 'Last 7 days', days: 7 },
      { label: 'Last 30 days', days: 30 },
      { label: 'Last 90 days', days: 90 },
    ],
  },
];

const buildProductVariables = ({
  first,
  filter,
  after,
  before,
  filters,
}: {
  first: number;
  filter: string;
  after?: string;
  before?: string;
  filters: AppliedFilters;
}) => {
  const status = filters.status?.type === 'enum' ? filters.status.values : [];
  const review = filters.review?.type === 'enum' ? filters.review.values : [];
  const productType = filters.productType?.type === 'enum' ? filters.productType.values[0] : undefined;
  const price = filters.price?.type === 'number' ? filters.price : undefined;
  const created = filters.created?.type === 'date' ? filters.created : undefined;

  return {
    first,
    filter,
    after,
    before,
    productStatus: status.length > 0 ? (status as ProductStatus[]) : undefined,
    reviewStatus: review.length > 0 ? (review as ProductReviewStatus[]) : undefined,
    productType: productType as ProductType | undefined,
    // The filter collects dollars — it is a "$" field a seller types 25 into —
    // and `priceMin`/`priceMax` range over the stored price, which is integer
    // cents (MPPRO-45). Converted here, at the one place the typed figure
    // becomes a query variable; sending 25 would have matched 25 cents.
    // Zero is a real bound, so these check for null rather than falsiness.
    priceMin: price?.min != null ? Math.round(price.min * 100) : undefined,
    priceMax: price?.max != null ? Math.round(price.max * 100) : undefined,
    createdAfter: created?.presetDays !== undefined ? presetDateIso(created.presetDays) : created?.from,
    createdBefore: created?.presetDays !== undefined ? undefined : created?.to,
  };
};

/** What the seller sees is the pending edit if there is one, else what's live. */
const shown = (product: Product) => product.pending ?? product.current;

const coverUrl = (product: Product): string | undefined => {
  const images = shown(product)?.images ?? [];
  const cover = images.find((image) => image.isFeatured) ?? images[0];
  return cover?.image?.thumbnailUrl ?? undefined;
};

/**
 * A product's price is its cheapest variant — "from $89.00" when they differ.
 *
 * `variant.price` is integer cents (MPPRO-45), so the comparison is on cents
 * and only the winner is converted, by `formatCents`.
 */
const priceLabel = (product: Product, currencyCode: string): string => {
  const prices = (shown(product)?.variants ?? []).map((variant) => variant.price);
  if (prices.length === 0) return '—';
  const min = Math.min(...prices);
  const formatted = formatCents({ amount: min, currencyCode });
  return Math.max(...prices) > min ? `from ${formatted}` : formatted;
};

const columns = (currencyCode: string): DataListColumn<Product>[] => [
  {
    header: 'Product',
    className: 'w-[42%]',
    cell: (product) => (
      <span className="flex min-w-0 items-center gap-3">
        <Thumb src={coverUrl(product)} alt="" />
        <span className="truncate font-semibold text-foreground">
          {shown(product)?.title || product.idAlias}
        </span>
      </span>
    ),
  },
  {
    header: 'Status',
    cell: (product) => (
      <ProductStateBadge status={product.status} reviewStatus={product.reviewStatus} />
    ),
  },
  {
    header: 'Type',
    cell: (product) => (
      <span className="text-muted-foreground">
        {shown(product)?.productType === ProductType.Digital ? 'Digital' : 'Physical'}
      </span>
    ),
  },
  {
    header: 'Price',
    align: 'right',
    cell: (product) => (
      <span className="tabular-nums text-foreground">{priceLabel(product, currencyCode)}</span>
    ),
  },
  {
    header: 'Updated',
    align: 'right',
    cell: (product) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatRelativeTime(product.updatedAt)}
      </span>
    ),
  },
];

export const Products: React.FC = () => {
  const navigate = useNavigate();
  const currencyCode = useShopCurrency();
  const list = useListView<ProductsData, Product, AppliedFilters>({
    query: PRODUCTS_QUERY,
    selectConnection: selectProductConnection,
    buildVariables: buildProductVariables,
    initialFilters: {},
    // Six, as the design draws. At five the last product fell to page two
    // and the screen looked shorter than it is.
    pageSize: 6,
  });

  const hasFilters = Object.keys(list.filters).length > 0;

  return (
    <AdminUI>
      <SaveBarBlocker>
        <div className="min-h-screen">
          {/*
            No count row. It listed Active / Awaiting approval / Changes
            requested / Archived under the heading, and review asked for it to
            go: "get rid of that completely, I don't wanna show that line at
            all." The states it counted are still reachable — Status and
            Review are both fields in the filter builder below.
          */}
          <CommandHeader
            title="Products"
            actions={
              <Button data-testid="add-product-button" onClick={() => navigate('/products/add')}>
                Add product
              </Button>
            }
          />

          <div className="mx-auto max-w-7xl px-6 py-5">
            <DataList
              variant="open"
              testId="products-list"
              list={list}
              columns={columns(currencyCode)}
              getRowId={(product) => product.id}
              onRowClick={(product) => navigate(`/products/${product.id}`)}
              searchPlaceholder="Search title or SKU…"
              sortLabel="Sorted by newest"
              matchCount={hasFilters ? list.count : undefined}
              filterSlot={
                <FilterBuilder
                  fields={FILTER_FIELDS}
                  filters={list.filters}
                  onChange={list.setFilters}
                />
              }
              emptyNarrowed={hasFilters}
              emptyTitle={hasFilters ? 'Nothing matches these filters' : 'Add your first product'}
              emptyDescription={
                hasFilters
                  ? undefined
                  : 'Products go to the shop for a quick review, then appear in the store.'
              }
              // With filters applied, the way out is to clear them — offering
              // "Add product" there would answer a question nobody asked.
              emptyAction={
                hasFilters
                  ? { label: 'Clear filters', onAction: () => list.setFilters({}) }
                  : { label: 'Add product', onAction: () => navigate('/products/add') }
              }
            />
          </div>
        </div>
      </SaveBarBlocker>
    </AdminUI>
  );
};
